"use strict";
import classNames from "classnames";
import React, {
  useState
} from "react";
import { Transition } from "react-transition-group";
import { Tick, IconSize, ListMarkerSquare, Warning } from "../../icons";
import { forceReflow } from "../../../utils";
import { darkTheme, duration, lightTheme } from "../../../tokens/tokens-css";
import { Label } from "../Label";
import * as styles from "./Checkbox-css";
import Tooltip from "../Tooltip/Tooltip";
import { Expandable } from "../../behaviors/Expandable";
import { Text } from "../Text";
export function Checkbox({
  size = "s",
  theme,
  id,
  label,
  checked,
  disabled,
  labelHidden,
  role,
  onChange,
  onFocus,
  onBlur,
  className,
  tooltip,
  error,
  ...props
}) {
  const [showTooltip, setShowTooltip] = useState(void 0);
  const checkboxId = `checkbox-${id}`;
  const labelId = `checkbox-${id}-label`;
  const tooltipId = `checkbox-${id}-tooltip`;
  const errorId = `checkbox-${id}-error`;
  const handleChange = (event) => {
    onChange(!checked, id, event);
  };
  const renderCheckbox = () => /* @__PURE__ */ React.createElement(
    "button",
    {
      className: styles.checkbox,
      onClick: handleChange,
      role: role ?? "checkbox",
      "aria-labelledby": labelId,
      "aria-checked": checked,
      "aria-disabled": disabled,
      "aria-describedby": error ? errorId : void 0,
      "aria-invalid": !!error,
      disabled,
      onFocus,
      onBlur,
      id: checkboxId,
      type: "button"
    },
    /* @__PURE__ */ React.createElement(Transition, { mountOnEnter: true, unmountOnExit: true, in: checked === true, timeout: duration.m, onEnter: forceReflow }, (status) => /* @__PURE__ */ React.createElement(
      Tick,
      {
        className: styles.indicator({ visible: ["entering", "entered"].includes(status) }),
        size: IconSize.Small
      }
    )),
    /* @__PURE__ */ React.createElement(Transition, { mountOnEnter: true, unmountOnExit: true, in: checked === "mixed", timeout: duration.m, onEnter: forceReflow }, (status) => /* @__PURE__ */ React.createElement(
      ListMarkerSquare,
      {
        className: styles.indicator({ visible: ["entering", "entered"].includes(status) }),
        size: IconSize.Small
      }
    ))
  );
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      className: classNames(
        styles.container,
        {
          [lightTheme]: theme === "light",
          [darkTheme]: theme === "dark"
        },
        className
      )
    },
    /* @__PURE__ */ React.createElement(
      "div",
      {
        className: styles.content({ disabled, size }),
        onMouseEnter: () => setShowTooltip(true),
        onMouseLeave: () => setShowTooltip(false),
        onFocus: () => setShowTooltip(true),
        onBlur: () => setShowTooltip(false),
        tabIndex: disabled ? 0 : void 0,
        "aria-describedby": disabled ? tooltipId : void 0,
        ...props
      },
      !tooltip && renderCheckbox(),
      !!tooltip && /* @__PURE__ */ React.createElement(Tooltip, { ...tooltip, show: tooltip.show || showTooltip }, renderCheckbox()),
      /* @__PURE__ */ React.createElement(
        Label,
        {
          long: true,
          disabled,
          hidden: labelHidden,
          className: styles.label({ hidden: labelHidden }),
          element: "label",
          htmlFor: checkboxId,
          id: labelId
        },
        label
      )
    ),
    /* @__PURE__ */ React.createElement(Expandable, { expanded: !!error, timeout: duration.s }, ({ expandableElementRef }) => /* @__PURE__ */ React.createElement("div", { "aria-live": "polite", ref: expandableElementRef, id: errorId, className: styles.error }, /* @__PURE__ */ React.createElement(Warning, { "aria-hidden": true, size: IconSize.Small, color: "critical", className: styles.errorIcon }), /* @__PURE__ */ React.createElement(Text, { size: "s", lineHeight: "compact" }, error)))
  );
}
